import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { TrophyIcon, BookOpenIcon, Building2Icon, GraduationCapIcon, GlobeIcon, BriefcaseIcon } from 'lucide-react';
import { ThreeDBackground } from './ThreeDBackground';
export function ImpactMetrics() {
  const metrics = [{
    icon: <TrophyIcon size={28} className="text-gold" data-id="element-205" />,
    number: '40+',
    label: 'Years of Distinguished Leadership',
    subtext: 'Clinical, academic, and corporate practice spanning four decades.'
  }, {
    icon: <BookOpenIcon size={28} className="text-gold" data-id="element-206" />,
    number: '100+',
    label: 'Peer-Reviewed Publications',
    subtext: 'Landmark contributions to global oral and maxillofacial science.'
  }, {
    icon: <Building2Icon size={28} className="text-gold" data-id="element-207" />,
    number: '12 yrs',
    label: 'Scaling ClearChoice',
    subtext: 'Co-founded and scaled a national specialty healthcare platform.'
  }, {
    icon: <GraduationCapIcon size={28} className="text-gold" data-id="element-208" />,
    number: '44 yrs',
    label: 'Academic Affiliation',
    subtext: 'University of Colorado, Utah, Michigan, and Northwestern.'
  }, {
    icon: <GlobeIcon size={28} className="text-gold" data-id="element-209" />,
    number: '4',
    label: 'Continents Reached',
    subtext: 'Keynote speaker across North America, Europe, Asia, and the Middle East.'
  }, {
    icon: <BriefcaseIcon size={28} className="text-gold" data-id="element-210" />,
    number: '6+',
    label: 'Active Board Roles',
    subtext: 'Governance leadership across MedTech, academic, and investment platforms.'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <section className="py-24 md:py-32 bg-navy-rich relative overflow-hidden" data-id="element-211">
      {/* Background gradient and watermark */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-rich to-navy opacity-90" data-id="element-212"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-serif text-[25rem] text-gold opacity-[0.02] select-none pointer-events-none" data-id="element-213">
        OJ
      </div>

      {/* 3D Animated Background */}
      <ThreeDBackground variant="metrics" data-id="element-214" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-215">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-20 text-center flex flex-col items-center" data-id="element-216">
          <div className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center mb-6" data-id="element-217">
            <span className="font-serif text-gold text-sm font-semibold" data-id="element-218">
              OJ
            </span>
          </div>
          <h2 className="font-sans text-gold text-sm uppercase tracking-widest font-medium mb-4" data-id="element-219">
            By the Numbers
          </h2>
          <h3 className="font-serif text-4xl md:text-5xl text-white font-medium leading-tight mb-6" data-id="element-220">
            Defining Outcomes. Quantifying Impact.
          </h3>
          <p className="font-sans text-white/60 text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-221">
            A career measured not just in years, but in institutions built,
            standards set, and futures shaped.
          </p>
        </motion.div>

        {/* Metrics Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-222">
          {metrics.map((metric, index) => <motion.div key={index} variants={itemVariants} className="relative bg-white/5 backdrop-blur-sm border border-white/10 p-8 md:p-10 flex flex-col items-center text-center group hover:bg-white/10 hover:border-gold/30 transition-all duration-300" data-id="element-223">
              {/* Mini gold corner accents */}
              <div className="absolute top-0 left-0 w-4 h-4 border-l border-t border-gold/40 group-hover:border-gold transition-colors" data-id="element-224"></div>
              <div className="absolute top-0 right-0 w-4 h-4 border-r border-t border-gold/40 group-hover:border-gold transition-colors" data-id="element-225"></div>
              <div className="absolute bottom-0 left-0 w-4 h-4 border-l border-b border-gold/40 group-hover:border-gold transition-colors" data-id="element-226"></div>
              <div className="absolute bottom-0 right-0 w-4 h-4 border-r border-b border-gold/40 group-hover:border-gold transition-colors" data-id="element-227"></div>

              <div className="w-16 h-16 rounded-full border border-gold/30 flex items-center justify-center mb-6 group-hover:border-gold group-hover:bg-gold/5 transition-colors" data-id="element-228">
                {metric.icon}
              </div>
              <span className="font-serif text-5xl md:text-6xl text-gold mb-4 block" data-id="element-229">
                {metric.number}
              </span>
              <div className="w-12 h-px bg-gold/30 mb-4" data-id="element-230"></div>
              <h4 className="font-sans text-white uppercase tracking-widest text-sm font-semibold mb-3" data-id="element-231">
                {metric.label}
              </h4>
              <p className="font-sans text-white/50 text-sm leading-relaxed" data-id="element-232">
                {metric.subtext}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Closing quote */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.8,
        duration: 0.8
      }} className="mt-20 text-center" data-id="element-233">
          <p className="font-serif italic text-gold-bright/90 text-2xl md:text-3xl leading-snug max-w-4xl mx-auto" data-id="element-234">
            Setting benchmarks. Building enterprises. Shaping global standards
            of care.
          </p>
        </motion.div>
      </div>
    </section>;
}